import {
  ACCENT_COLORS,
  GREENC_COLORS,
  ORANGEC_COLORS,
  PINKC_COLORS,
  PRIMARY_COLORS,
  REDC_COLORS,
  S800_COLORS,
  SECONDARY_COLORS,
  VIOLETC_COLORS,
  YELLOWC_COLORS,
} from "@monthly/theme";
import { useTheme } from "@mui/material";
import HighchartsReact from "highcharts-react-official";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { addWatermarkToHighchartsExport } from "../chartWatermark";
import Highcharts from "../HighChartsConfig";
import {
  formatNumberWithSeparators,
  truncateNumber,
} from "../utils/formatting";

export interface ColorPalette {
  D30: string;
  D20: string;
  D10: string;
  main: string;
  L10: string;
  L20: string;
  L30: string;
  L40: string;
  L50: string;
  L60: string;
  L70: string;
  L80: string;
  L90: string;
}

export type ColorName =
  | "primary"
  | "secondary"
  | "accent"
  | "yellowc"
  | "redc"
  | "greenc"
  | "violetc"
  | "orangec"
  | "pinkc"
  | "s800";

const COLOR_PALETTES: Record<ColorName, ColorPalette> = {
  primary: PRIMARY_COLORS,
  secondary: SECONDARY_COLORS,
  accent: ACCENT_COLORS,
  yellowc: YELLOWC_COLORS,
  redc: REDC_COLORS,
  greenc: GREENC_COLORS,
  violetc: VIOLETC_COLORS,
  orangec: ORANGEC_COLORS,
  pinkc: PINKC_COLORS,
  s800: S800_COLORS,
};

export function getColorFromPalette(colorName: ColorName): string {
  return COLOR_PALETTES[colorName].main;
}

export interface ColumnChartGradientProps {
  categories: string[];
  series: {
    name: string;
    data: number[];
  }[];
  baseColor?: ColorName;
  height?: number | string;
  stacked?: boolean;
  showLegend?: boolean;
  showStackedLabels?: boolean;
  showDataLabels?: boolean;
  isPercentage?: boolean;
  decimalPlaces?: number;
  customStackTotals?: number[];
  units?: string;
}

export interface ColumnChartGradientRef {
  downloadImage: (filename?: string) => void;
}

export const ColumnChartGradient = forwardRef<
  ColumnChartGradientRef,
  ColumnChartGradientProps
>(
  (
    {
      categories,
      series,
      baseColor: colorName = "primary",
      height = 250,
      stacked = false,
      showLegend = true,
      showStackedLabels = false,
      showDataLabels = false,
      isPercentage = false,
      decimalPlaces = 1,
      customStackTotals,
      units,
    },
    ref,
  ) => {
    const finalDecimalPlaces = decimalPlaces !== undefined ? decimalPlaces : 1;
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const theme = useTheme();

    // Get the color palette based on colorName prop
    const colorPalette = COLOR_PALETTES[colorName];

    // Create color array from D30 to L90 in steps of 10
    const colorArray = [
      colorPalette.D30,
      colorPalette.D20,
      colorPalette.D10,
      colorPalette.main,
      colorPalette.L10,
      colorPalette.L20,
      colorPalette.L30,
      colorPalette.L40,
      colorPalette.L50,
      colorPalette.L60,
      colorPalette.L70,
      colorPalette.L80,
      colorPalette.L90,
    ];

    // Map each series to a color based on its position in the stack
    // Series 0 (bottom) gets D30, Series 1 gets D20, Series 2 gets D10, etc.
    const getColorForSeries = (seriesIndex: number) => {
      if (seriesIndex < colorArray.length) {
        return colorArray[seriesIndex];
      }
      // If there are more series than colors, cycle back
      return colorArray[seriesIndex % colorArray.length];
    };

    // Determine text color based on background color brightness
    // L40 and lighter backgrounds use dark text (D30)
    const getTextColorForSeries = (seriesIndex: number) => {
      const colorIndex =
        seriesIndex < colorArray.length
          ? seriesIndex
          : seriesIndex % colorArray.length;
      // L40 is at index 7 (D30, D20, D10, main, L10, L20, L30, L40)
      // So index >= 7 means use dark text
      if (colorIndex >= 7) {
        return colorPalette.D30;
      }
      return "#FFFFFF";
    };

    useImperativeHandle(ref, () => ({
      downloadImage: (filename = "chart") => {
        if (chartRef.current?.chart) {
          addWatermarkToHighchartsExport(chartRef.current.chart, filename);
        }
      },
    }));

    // Process series with gradient fills
    const processedSeries = series.map((s, seriesIndex) => {
      const truncatedData = s.data.map((value) =>
        truncateNumber(value, decimalPlaces),
      );

      const processedData = truncatedData.map((value) => ({
        y: value,
      }));

      const seriesColor = getColorForSeries(seriesIndex);
      const textColor = getTextColorForSeries(seriesIndex);

      // For stacked charts with labels
      if (showStackedLabels && stacked) {
        return {
          type: "column" as const,
          name: s.name,
          data: processedData,
          color: seriesColor,
          colorByPoint: false,
          dataLabels: {
            enabled: true,
            formatter: function () {
              const value = truncateNumber(
                (this as { y?: number }).y ?? 0,
                decimalPlaces,
              );
              return isPercentage
                ? `${formatNumberWithSeparators(value, finalDecimalPlaces)}%`
                : formatNumberWithSeparators(value, finalDecimalPlaces);
            },
            inside: true,
            verticalAlign: "middle" as const,
            y: 0,
            align: "center" as const,
            style: {
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.caption.fontSize,
              fontWeight: theme.typography.fontWeightSemiBold,
              color: textColor,
              textOutline: "none",
            },
          },
        };
      }

      return {
        type: "column" as const,
        name: s.name,
        data: processedData,
        color: seriesColor,
        colorByPoint: false,
      };
    });

    const options: Highcharts.Options = {
      chart: {
        type: "column",
        height,
        spacing: [0, 0, 0, 0],
        marginTop: showLegend ? 60 : 10,
      },
      title: {
        text: undefined,
      },
      tooltip: {
        shared: false,
        useHTML: true,
        backgroundColor: "#FFFFFF",
        borderColor: theme.palette.divider,
        borderRadius: 4,
        padding: 6,
        style: {
          color: theme.palette.text.primary,
        },
        formatter: function () {
          const categoryName = this.key;
          const seriesName = this.series.name;
          const value = this.y;
          const seriesColor = this.series.color;

          const fontSize = theme.typography.overline.fontSize;
          const fontWeight = theme.typography.overline.fontWeight;
          const fontWeightSemiBold = theme.typography.fontWeightSemiBold;
          const letterSpacing = theme.typography.overline.letterSpacing;
          const primaryColor = theme.palette.text.primary;
          const secondaryColor = theme.palette.secondary[400];
          const suffix = units ? ` ${units}` : "";

          return `
            <div>
              <div style="font-size: ${fontSize}; font-weight: ${fontWeightSemiBold}; letter-spacing: ${letterSpacing}; color: ${primaryColor}; margin-bottom: 4px;">
              ${categoryName}
              </div>
              <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 8px; height: 8px; background-color: ${seriesColor}; border-radius: 50%; margin-right: 3px;"></span>
              <span style="font-size: ${fontSize}; font-weight: ${fontWeight}; color: ${secondaryColor};">
                ${seriesName}
              </span>
              <span style="font-size: ${fontSize}; font-weight: ${fontWeight}; color: ${primaryColor};">
               &nbsp;- ${value}${suffix}
              </span>
              </div>
            </div>
            `;
        },
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: {
          style: {
            fontFamily: `${theme.typography.caption.fontFamily}`,
            fontSize: `${theme.typography.caption.fontSize}`,
            fontWeight: `${theme.typography.caption.fontWeight}`,
            color: `${theme.palette.text.primary}`,
          },
        },
      },
      yAxis: {
        reversedStacks: false,
        title: {
          text: undefined,
        },
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
        lineWidth: 0,
        stackLabels: {
          enabled: showStackedLabels && stacked,
          formatter: function () {
            const context = this as { total?: number; x?: number };
            const value =
              customStackTotals && context.x !== undefined
                ? (customStackTotals[context.x] ?? context.total ?? 0)
                : (context.total ?? 0);
            const truncatedValue = truncateNumber(value, finalDecimalPlaces);
            return isPercentage
              ? `${formatNumberWithSeparators(truncatedValue, finalDecimalPlaces)}%`
              : formatNumberWithSeparators(truncatedValue, finalDecimalPlaces);
          },
          style: {
            fontFamily: `${theme.typography.caption.fontFamily}`,
            fontSize: `${theme.typography.caption.fontSize}`,
            fontWeight: `${theme.typography.caption.fontWeight}`,
            color: `${theme.palette.text.primary}`,
          },
          y: -5,
        },
      },
      legend: {
        enabled: showLegend,
        align: "left",
        verticalAlign: "top",
        floating: true,
        y: 0,
        x: 0,
        symbolRadius: 16,
        itemStyle: {
          fontFamily: `${theme.typography.caption.fontFamily}`,
          fontSize: `${theme.typography.caption.fontSize}`,
          fontWeight: `${theme.typography.caption.fontWeight}`,
          color: `${theme.palette.text.primary}`,
        },
      },
      plotOptions: {
        column: {
          stacking: stacked ? "normal" : undefined,
          pointPadding: 0.05,
          groupPadding: 0.1,
          borderRadius: 4,
          borderWidth: 1,
          colorByPoint: false,
          dataLabels: {
            enabled: showDataLabels,
            formatter: function () {
              const value = (this as { y?: number }).y ?? 0;
              const truncated = truncateNumber(value, finalDecimalPlaces);
              const formatted = truncated.toLocaleString("en-US", {
                minimumFractionDigits: finalDecimalPlaces,
                maximumFractionDigits: finalDecimalPlaces,
              });
              return isPercentage ? `${formatted}%` : formatted;
            },
            style: {
              fontFamily: `${theme.typography.caption.fontFamily}`,
              fontSize: `${theme.typography.caption.fontSize}`,
              fontWeight: `${theme.typography.caption.fontWeight}`,
              color: `${theme.palette.text.primary}`,
            },
            y: -5,
          },
        },
      },
      series: processedSeries as Highcharts.SeriesOptionsType[],
    };

    useEffect(() => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.reflow();
      }
    }, []);

    return (
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
      />
    );
  },
);

ColumnChartGradient.displayName = "ColumnChartGradient";
